/**
 * BE-UX - Backend UX Tools
 * Admin-facing page for quick updates to backend elements without full CRUD forms.
 * Trip main_image: upload file → main_image_blob_id.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Grid,
  useTheme,
  Divider,
  Chip
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { tripsService, uploadsService } from '../../services';
import { getTripImageUrl } from '../../utils/tripImage';

const BEUX = () => {
  const theme = useTheme();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [pendingFiles, setPendingFiles] = useState({}); // { tripId: File }

  const fetchTrips = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await tripsService.getTrips({ per_page: 100 });
      const tripList = response?.trips || response?.data?.trips || [];
      setTrips(tripList);
    } catch (err) {
      setError(err.error?.message || err.message || 'Failed to load trips');
      console.error('BE-UX: Error fetching trips:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrips();
  }, []);

  const handleFileSelect = (tripId, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (JPEG, PNG, GIF, WebP)');
      return;
    }
    setError(null);
    setPendingFiles((prev) => ({ ...prev, [tripId]: file }));
  };

  const handleUpdateImage = async (trip) => {
    const file = pendingFiles[trip.id];
    if (!file) return;

    try {
      setUpdatingId(trip.id);
      setSuccess(null);
      setError(null);
      const uploadRes = await uploadsService.uploadImage(file);
      const blobId = uploadRes?.data?.id ?? uploadRes?.id;
      if (blobId == null) {
        setError('Upload succeeded but no blob ID was returned');
        setUpdatingId(null);
        return;
      }
      await tripsService.updateTrip(trip.id, { main_image_blob_id: blobId });
      setSuccess(`Updated image for "${trip.title}"`);
      setPendingFiles((prev) => ({ ...prev, [trip.id]: null }));
      const input = document.getElementById(`beux-upload-${trip.id}`);
      if (input) input.value = '';
      fetchTrips();
    } catch (err) {
      const msg = err.error?.message || err.message || 'Failed to update image';
      setError(msg.includes('401') || msg.includes('Unauthorized') ? 'Please log in as admin to update images.' : msg);
    } finally {
      setUpdatingId(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" fontWeight={600}>
          BE-UX
        </Typography>
        <Chip
          label="Backend UX Tools"
          size="small"
          sx={{ bgcolor: theme.palette.primary.light, color: 'white' }}
        />
        <Button
          startIcon={<RefreshIcon />}
          onClick={fetchTrips}
          variant="outlined"
          size="small"
        >
          Refresh
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Quick tools to update backend elements. Trip main images: upload file, then update with main_image_blob_id.
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }} icon={<CheckCircleIcon />}>
          {success}
        </Alert>
      )}

      <Divider sx={{ mb: 3 }} />

      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Trip Main Images
      </Typography>

      {trips.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No trips found.</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {trips.map((trip) => {
            const tripImageUrl = getTripImageUrl(trip);
            return (
            <Grid item xs={12} md={6} key={trip.id}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: 2 }
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                    {tripImageUrl && (
                      <Box
                        component="img"
                        src={tripImageUrl}
                        alt={trip.title}
                        sx={{
                          width: 80,
                          height: 60,
                          objectFit: 'cover',
                          borderRadius: 1,
                          bgcolor: 'grey.200'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        ID: {trip.id}
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={600} noWrap>
                        {trip.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {pendingFiles[trip.id] ? `Selected: ${pendingFiles[trip.id].name}` : 'Select image to update'}
                      </Typography>
                    </Box>
                  </Box>
                  <input
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    type="file"
                    id={`beux-upload-${trip.id}`}
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileSelect(trip.id, e.target.files?.[0])}
                    disabled={updatingId === trip.id}
                  />
                  <label htmlFor={`beux-upload-${trip.id}`}>
                    <Button
                      variant="outlined"
                      component="span"
                      size="small"
                      startIcon={updatingId === trip.id ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
                      disabled={updatingId === trip.id}
                      fullWidth
                    >
                      {pendingFiles[trip.id] ? 'Change file' : 'Choose image'}
                    </Button>
                  </label>
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2 }}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={updatingId === trip.id ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    onClick={() => handleUpdateImage(trip)}
                    disabled={!pendingFiles[trip.id] || updatingId === trip.id}
                  >
                    {updatingId === trip.id ? 'Updating...' : 'Update'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default BEUX;
